import PropTypes from 'prop-types'
import Select, { components } from 'react-select'
import { useTranslation } from 'react-i18next'
import { CalendarBlankIcon, CaretDownIcon, ClockIcon, XIcon } from '@phosphor-icons/react'

import DateField from '../../FormLegacy/FormFieldsV2/DateField'
import { getScheduledMinDate, getScheduledTimeOptions } from './helpers'

const CompactClearIndicator = (props) => (
  <components.ClearIndicator {...props}>
    <XIcon size={20} />
  </components.ClearIndicator>
)

const CompactDropdownIndicator = (props) =>
  props.hasValue ? null : (
    <components.DropdownIndicator {...props}>
      <CaretDownIcon size={20} />
    </components.DropdownIndicator>
  )

const COMPACT_SELECT_COMPONENTS = {
  ClearIndicator: CompactClearIndicator,
  DropdownIndicator: CompactDropdownIndicator,
  IndicatorSeparator: null,
}

const FieldHeader = ({ title, isError, isRequired }) => {
  const { t } = useTranslation(['maintenance'])

  return (
    <div className={`row-header ${isError ? 'row-header--error' : ''}`}>
      {title} {!isRequired && <span>{t('maintenance:form.requestDetails.optional')}</span>}
    </div>
  )
}

export const ScheduledDateField = ({
  value,
  timezone,
  onChange,
  isError,
  isRequired,
  disabled,
  isCompact,
}) => {
  const { t } = useTranslation(['maintenance'])
  const hasValue = !!value

  return (
    <div className={`scheduled-date-time__field ${isError ? 'is-error' : ''}`}>
      {!isCompact && (
        <FieldHeader
          title={t('maintenance:form.pmsSettings.scheduledDate.date.label')}
          isError={isError}
          isRequired={isRequired}
        />
      )}
      <div className="scheduled-date-time__control">
        <DateField
          name="scheduledDate"
          value={value}
          onChange={({ target }) => onChange(target.value)}
          placeholderText={t('maintenance:form.pmsSettings.scheduledDate.date.placeholder')}
          minDate={getScheduledMinDate(timezone)}
          popperPlacement="bottom-start"
          showYearDropdown
          scrollableYearDropdown
          isError={isError}
          disabled={disabled}
        />
        {!isCompact && <CalendarBlankIcon className="scheduled-date-time__icon" size={20} />}
        {isCompact &&
          (hasValue && !disabled ? (
            <button
              type="button"
              className="scheduled-date-time__clear"
              aria-label={t('maintenance:form.pmsSettings.scheduledDate.date.clear')}
              onClick={() => onChange(null)}
            >
              <XIcon size={20} />
            </button>
          ) : (
            <CaretDownIcon className="scheduled-date-time__caret" size={20} />
          ))}
      </div>
      {isError && (
        <span className="label--error">
          {isRequired
            ? t('maintenance:form.pmsSettings.scheduledDate.date.required')
            : t('maintenance:form.pmsSettings.scheduledDate.date.missingForTime')}
        </span>
      )}
    </div>
  )
}

// Note: `date` is the picked day — needed to disable the time slots that are already past.
export const ScheduledTimeField = ({
  value,
  date,
  timezone,
  onChange,
  isError,
  isRequired,
  disabled,
  isCompact,
}) => {
  const { t } = useTranslation(['maintenance'])
  const timeOptions = getScheduledTimeOptions(date, timezone)

  return (
    <div className={`scheduled-date-time__field ${isError ? 'is-error' : ''}`}>
      {!isCompact && (
        <FieldHeader
          title={t('maintenance:form.pmsSettings.scheduledDate.time.label')}
          isError={isError}
          isRequired={isRequired}
        />
      )}
      <div className="scheduled-date-time__control">
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          name="scheduledTime"
          options={timeOptions}
          value={timeOptions.find((option) => option.value === value) || null}
          // Note: a cleared select reports `null` instead of an option.
          onChange={(option) => onChange(option ? option.value : null)}
          placeholder={t('maintenance:form.pmsSettings.scheduledDate.time.placeholder')}
          isSearchable={false}
          isDisabled={disabled}
          isClearable={isCompact}
          components={isCompact ? COMPACT_SELECT_COMPONENTS : undefined}
        />
        {!isCompact && <ClockIcon className="scheduled-date-time__icon" size={20} />}
      </div>
      {isError && (
        <span className="label--error">
          {isRequired
            ? t('maintenance:form.pmsSettings.scheduledDate.time.required')
            : t('maintenance:form.pmsSettings.scheduledDate.time.missingForDate')}
        </span>
      )}
    </div>
  )
}

const ScheduledDateTimeFields = ({
  date,
  time,
  timezone,
  isRequired,
  onDateChange,
  onTimeChange,
  isDateError,
  isTimeError,
  disabled,
}) => (
  <div className="scheduled-date-time">
    <ScheduledDateField
      value={date}
      timezone={timezone}
      onChange={onDateChange}
      isError={isDateError}
      isRequired={isRequired}
      disabled={disabled}
    />
    <ScheduledTimeField
      value={time}
      date={date}
      timezone={timezone}
      onChange={onTimeChange}
      isError={isTimeError}
      isRequired={isRequired}
      disabled={disabled}
    />
  </div>
)

const fieldPropTypes = {
  isError: PropTypes.bool,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  isCompact: PropTypes.bool,
  timezone: PropTypes.string,
  onChange: PropTypes.func,
}

FieldHeader.propTypes = {
  title: PropTypes.string,
  isError: PropTypes.bool,
  isRequired: PropTypes.bool,
}

ScheduledDateField.propTypes = { ...fieldPropTypes, value: PropTypes.instanceOf(Date) }

ScheduledTimeField.propTypes = {
  ...fieldPropTypes,
  value: PropTypes.number,
  date: PropTypes.instanceOf(Date),
}

CompactDropdownIndicator.propTypes = { hasValue: PropTypes.bool }

ScheduledDateTimeFields.propTypes = {
  date: PropTypes.instanceOf(Date),
  time: PropTypes.number,
  timezone: PropTypes.string,
  isRequired: PropTypes.bool,
  onDateChange: PropTypes.func,
  onTimeChange: PropTypes.func,
  isDateError: PropTypes.bool,
  isTimeError: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default ScheduledDateTimeFields
